import React from "react";

import { css } from "@emotion/css";
import { CellContext, type SortingFn } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { TelemonitoringTags } from "@/models/CarePlanModel.ts";
import { PatientListItem } from "@/models/PatientSummaryModel.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { TelemonitoringActivityTag } from "@/uiKit/molecules/TelemonitoringActivityTag.tsx";

export type TelemonitoringColumnProps = CellContext<PatientListItem, TelemonitoringTags[]>;

export const TelemonitoringColumn: React.FC<TelemonitoringColumnProps> = ({ getValue }) => {
  const tagList = getValue();
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.container}>
      <TelemonitoringActivityTag tags={tagList} />
    </div>
  );
};

export const TelemonitoringHeader: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeHeaderStyles);
  return (
    <div className={styles.container}>
      <Typography>{t("pages.patients.telemonitoring")}</Typography>
    </div>
  );
};

const NO_TAG_RANK = 100;
const telemonitoringTagRank: Record<TelemonitoringTags, number> = {
  expired_recently: 0,
  expired: 1,
  expiring_soon: 2,
  no_glycemia_data: 3,
  nurse_asked_for_reinclusion: 4,
  active: 5,
  gestational_no_insulin: 6,
};

const mostUrgentTagRank = (tags: TelemonitoringTags[]) =>
  tags.reduce((min, tag) => Math.min(min, telemonitoringTagRank[tag]), NO_TAG_RANK);

export const sortByTelemonitoringTag: SortingFn<PatientListItem> = (rowA, rowB) =>
  mostUrgentTagRank(rowA.original.tags) - mostUrgentTagRank(rowB.original.tags);

const makeStyles = () => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
});

const makeHeaderStyles = () => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
});
